/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import React, {ForwardedRef, forwardRef, useContext, useImperativeHandle, useRef, useState} from 'react';
import {ErrorType, nullify, ResponseData, showDialog} from '@dvrd/dvr-controls';
import {onForcePing} from '../../../../util/events';
import {ActivateHandle, CancelType} from "../../../../util/interfaces";
import {setCancelReason} from "../../../../util/requestSender";
import CancelSubscription from "./cancelSubscription";
import {createErrorMessage} from "../../../../util/utils";
import {onShowConfirmSnackbar} from "../../../../util/renderUtil";
import {trackPlatformAction} from "../../../../util/analytics";
import {AppContext} from "../../../misc/appContext";

interface Props {
    onSuccess?: VoidFunction;
    type: CancelType;
}

function CancelSubscriptionController(props: Props, ref: ForwardedRef<ActivateHandle>) {
    const context = useContext(AppContext);
    const {account} = context.accountContext;
    const {type, onSuccess} = props;
    const [active, setActive] = useState(false);
    const [loading, setLoading] = useState(false);
    const [reason, setReason] = useState('');
    const reasonSaved = useRef<boolean>(false);
    const [error, setError] = useState<ErrorType>(null);

    function onClose() {
        setActive(false);
        setReason('');
    }

    function onChange(value: string) {
        setReason(value);
    }

    function onFinish(success: boolean) {
        return function () {
            if (reasonSaved.current && reason.length) {
                showDialog('Bedankt voor je feedback!', 'Bedankt', [{
                    label: 'Oké', onClick: () => {
                        onClose();
                        if (onSuccess && success) onSuccess();
                    }
                }]);
            } else {
                onClose();
                if (onSuccess && success) onSuccess();
            }
        }
    }

    function onResetError() {
        setError(null);
    }

    function onSubmit() {
        if (!validateValues()) return;
        if(!account) return onFinish(true)();
        setLoading(true);
        setCancelReason({
            data: {
                account_id: account.id,
                reason: nullify(reason),
                cancel_type: type,
            }, callback: (data: ResponseData) => {
                setLoading(false);
                if (data.success) {
                    trackPlatformAction('settings.subscription', 'cancel_subscription');
                    onShowConfirmSnackbar('Het abonnement is opgezegd');
                    reasonSaved.current = true;
                    onFinish(true)();
                } else
                    showDialog(createErrorMessage(data.message ?? 'Het stoppen van het abonnement is niet gelukt.'), 'Stopzetten mislukt')
            }
        });
    }

    function validateValues(): boolean {
        if (!reason) {
            setError('Graag nog wat feedback achter laten over waarom je weggaat.');
            return false;
        }
        return true;
    }

    function activate() {
        reasonSaved.current = false;
        setActive(true);
    }

    useImperativeHandle(ref, () => ({activate}));

    return (
        <CancelSubscription onSubmit={onSubmit} onCancel={onFinish(false)} onChange={onChange} reason={reason}
                            active={active} loading={loading} type={type} error={error} onResetError={onResetError}/>
    )
}

export default forwardRef<ActivateHandle, Props>(CancelSubscriptionController);